import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EventEditor from './EventEditor';
import { getUserFriendlyError, validateEventDetection } from '../../utils/validationUtils';
import {
  EditorContainer,
  EditorHeader,
  EditorTitle,
  Button,
  EnableToggleContainer,
  ToggleLabel,
  ToggleLabelText,
  ToggleLabelSubtext,
  ToggleSwitch,
  StyledToggleOn,
  StyledToggleOff,
  EventsListContainer,
  EventsListHeader,
  EventsListLabel,
  EmptyEventsPlaceholder,
  ValidationError
} from './EventDetection.styled';

const EventConfigEditor = ({
  initialConfigs,
  initialEnabled,
  onChange,
  eventMetadata,
  hideTitle,
  vibrationDirectionPreference,
  temperatureOnly,
  magnetometerAvailable,
  speedLimits,
  savedSpeedValues,
  onValidationChange,
  alarmThresholds,
  fetchingAlarms,
  dashboardCouplingEligibility,
  fetchingDashboardEligibility,
  user
}) => {
  const { t } = useTranslation();
  const configs = initialConfigs;
  const enabled = initialEnabled;

  // Validation state
  const [validationResult, setValidationResult] = useState({ isValid: true, errors: [], configErrors: {} });
  const [showValidation, setShowValidation] = useState(false);

  // Validate whenever configs or enabled status changes
  useEffect(() => {
    const eventDetection = {
      event_configs: configs,
      enabled
    };
    const result = validateEventDetection(eventDetection, enabled, speedLimits, savedSpeedValues);
    setValidationResult(result);

    // Show validation errors when enabled and validation fails
    // This includes the case where enabled=true but configs.length=0
    if (enabled && !result.isValid) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
    }

    // Notify parent component of validation state
    if (onValidationChange) {
      onValidationChange(result.isValid);
    }
  }, [configs, enabled, speedLimits, savedSpeedValues, onValidationChange]);

  const handleAddConfig = useCallback(() => {
    // Default to temperature sensor type when temperatureOnly is true
    const defaultMetric = temperatureOnly ? 'Temperature' : 'Vibration';
    const defaultMetricType = temperatureOnly ? 'Overall' : 'Accel RMS';
    const defaultThresholds = temperatureOnly ? { value: 0 } : { x: 8.0, y: 8.0, z: 8.0 };

    const newConfig = {
      conditions: [
        {
          metric: defaultMetric,
          metricType: defaultMetricType,
          operator: '>',
          thresholds: defaultThresholds
        }
      ],
      logic: 'AND',
      action: 'Immediate Upload'
    };
    const newConfigs = [...configs, newConfig];
    onChange(newConfigs, enabled);
  }, [configs, enabled, temperatureOnly, onChange]);

  const handleRemoveConfig = useCallback((index) => {
    const newConfigs = configs.filter((c, i) => i !== index);
    onChange(newConfigs, enabled);
  }, [configs, enabled, onChange]);

  const handleUpdateConfig = useCallback((index, updates) => {
    const newConfigs = configs.map((c, i) => (i === index ? { ...c, ...updates } : c));
    onChange(newConfigs, enabled);
  }, [configs, enabled, onChange]);

  const handleToggleEnabled = useCallback(() => {
    const newEnabled = !enabled;

    // If disabling, clear configs
    const newConfigs = newEnabled ? configs : [];
    onChange(newConfigs, newEnabled);

    // When enabling without configs, show validation to encourage adding events
    if (newEnabled && configs.length === 0) {
      setShowValidation(true);
    }
  }, [enabled, configs.length, onChange]);

  return (
    <EditorContainer>
      {!hideTitle && (
        <EditorHeader>
          <EditorTitle>{t('sensors.eventDetection.title')}</EditorTitle>
        </EditorHeader>
      )}

      <EnableToggleContainer>
        <ToggleLabel>
          <ToggleLabelText>
            {t('sensors.eventDetection.enableTitle')}
          </ToggleLabelText>
          <ToggleLabelSubtext>
            {t('sensors.eventDetection.enableSubtitle')}
          </ToggleLabelSubtext>
        </ToggleLabel>
        <ToggleSwitch
          onClick={handleToggleEnabled}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggleEnabled();
            }
          }}
        >
          {enabled ? <StyledToggleOn /> : <StyledToggleOff />}
        </ToggleSwitch>
      </EnableToggleContainer>

      {enabled && (
        <EventsListContainer>
          <EventsListHeader>
            <EventsListLabel>{t('sensors.eventDetection.eventsLabel')}</EventsListLabel>
            <Button variant="outline" size="sm" onClick={handleAddConfig}>
              {t('sensors.eventDetection.addEvent')}
            </Button>
          </EventsListHeader>

          {/* Global validation errors */}
          {showValidation && validationResult.errors.length > 0 && (
            <div style={{ marginBottom: '12px' }}>
              {validationResult.errors.map((error, idx) => (
                <ValidationError key={idx}>{getUserFriendlyError(error, t)}</ValidationError>
              ))}
            </div>
          )}

          {configs.length === 0 ? (
            <EmptyEventsPlaceholder>
              <p>
                {t('sensors.eventDetection.emptyEditorHint')}
              </p>
            </EmptyEventsPlaceholder>
          ) : (
            <div>
              {configs.map((config, index) => (
                <EventEditor
                  key={index}
                  event={config}
                  onUpdate={updates => handleUpdateConfig(index, updates)}
                  onRemove={() => handleRemoveConfig(index)}
                  showRemove={configs.length > 1}
                  eventMetadata={eventMetadata}
                  vibrationDirectionPreference={vibrationDirectionPreference}
                  temperatureOnly={temperatureOnly}
                  magnetometerAvailable={magnetometerAvailable}
                  validationErrors={showValidation && validationResult.configErrors[index] ? validationResult.configErrors[index] : null}
                  alarmThresholds={alarmThresholds}
                  fetchingAlarms={fetchingAlarms}
                  dashboardCouplingEligibility={dashboardCouplingEligibility}
                  fetchingDashboardEligibility={fetchingDashboardEligibility}
                  user={user}
                />
              ))}
            </div>
          )}
        </EventsListContainer>
      )}
    </EditorContainer>
  );
};

EventConfigEditor.propTypes = {
  initialConfigs: PropTypes.array.isRequired,
  initialEnabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  eventMetadata: PropTypes.object,
  hideTitle: PropTypes.bool,
  vibrationDirectionPreference: PropTypes.number,
  temperatureOnly: PropTypes.bool,
  magnetometerAvailable: PropTypes.bool,
  speedLimits: PropTypes.object,
  savedSpeedValues: PropTypes.arrayOf(PropTypes.number),
  onValidationChange: PropTypes.func,
  alarmThresholds: PropTypes.object,
  fetchingAlarms: PropTypes.bool,
  dashboardCouplingEligibility: PropTypes.object,
  fetchingDashboardEligibility: PropTypes.bool,
  user: PropTypes.object
};

EventConfigEditor.defaultProps = {
  initialEnabled: false,
  eventMetadata: null,
  hideTitle: false,
  vibrationDirectionPreference: 1,
  temperatureOnly: false,
  magnetometerAvailable: true,
  speedLimits: null,
  savedSpeedValues: [],
  onValidationChange: null,
  alarmThresholds: null,
  fetchingAlarms: false,
  dashboardCouplingEligibility: null,
  fetchingDashboardEligibility: false,
  user: null
};

export default EventConfigEditor;
